import React, {
  Fragment,
  type FunctionComponent,
  useCallback,
  useEffect,
  useState,
} from 'react';

export type OverlayPlacementType =
  | 'auto'
  | 'auto-start'
  | 'auto-end'
  | 'top'
  | 'top-start'
  | 'top-end'
  | 'right'
  | 'right-start'
  | 'right-end'
  | 'bottom'
  | 'bottom-start'
  | 'bottom-end'
  | 'left'
  | 'left-start'
  | 'left-end';

export type OverlayProps = {
  toggle: () => void;
  target: HTMLElement | string;
  isOpen: boolean;
  trigger?: string | Array<string>;
  placement?: OverlayPlacementType;
  delay?: number | {show: number; hide: number};
  offset?: string;
};

export type AttachOverlayChildProps = {
  ref: (instance: HTMLElement | null) => void;
  onClick?: () => void;
  onMouseOver?: () => void;
  onMouseOut?: () => void;
  onFocus?: () => void;
  onBlur?: () => void;
};

type AttachOverlayTargetProps = {
  trigger: string;
  overlay: FunctionComponent<{overlayProps: OverlayProps}>;
  children: FunctionComponent<{id: string}>;
  targetid: string;
};

/* A lightweight attach overlay to group a trigger (e.g. button) with the popover */
export function AttachOverlayTarget({
  overlay,
  children,
  trigger,
  targetid,
}: AttachOverlayTargetProps) {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const toggle = () => setIsOpen((isOpen) => !isOpen);

  return (
    <>
      {children({id: targetid})}
      {overlay({
        overlayProps: {
          trigger,
          toggle,
          isOpen,
          target: targetid,
        },
      })}
    </>
  );
}

type AttachOverlayProps = {
  trigger: string;
  overlay: FunctionComponent<{overlayProps: OverlayProps}>;
  children: FunctionComponent<AttachOverlayChildProps>;
};

export function AttachOverlay({
  overlay,
  children,
  trigger,
}: AttachOverlayProps) {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [targetNode, setTargetNode] = useState<null | HTMLElement>(null);
  const targetRef = useCallback<(instance: HTMLElement | null) => void>(
    (node: HTMLElement | null) => {
      setTargetNode(node);
    },
    []
  );

  const [mounted, setMounted] = useState<boolean>(false);
  useEffect(
    // lazy mount: only mount the overlay after its visible
    () => {
      if (isOpen && !mounted) setMounted(true);
    },
    [isOpen, targetNode]
  );

  const toggle = () => setIsOpen((isOpen) => !isOpen);

  const show = () => (!isOpen ? setIsOpen(true) : undefined);

  const hide = () => (isOpen ? setIsOpen(false) : undefined);

  const hasClickTrigger = trigger.indexOf('click') >= 0;
  const hasHoverTrigger = trigger.indexOf('hover') >= 0;
  const hasFocusTrigger = trigger.indexOf('focus') >= 0;
  return (
    <Fragment>
      {mounted && targetNode != null
        ? overlay({
            overlayProps: {
              trigger,
              toggle,
              isOpen,
              target: targetNode,
            },
          })
        : null}
      {children({
        ref: targetRef,
        onClick: hasClickTrigger ? toggle : undefined,
        onMouseOver: hasHoverTrigger ? show : undefined,
        onMouseOut: hasHoverTrigger ? hide : undefined,
        onFocus: hasFocusTrigger ? show : undefined,
        onBlur: hasFocusTrigger ? hide : undefined,
      })}
    </Fragment>
  );
}
